import { motion } from 'framer-motion'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useUTMParams } from '@/hooks/useUTMParams'


import { LoginForm } from '../../layout/login/LoginForm'

import { ContainerPage } from '@/components/common'
import { SubTitle } from '@/components/common/UI'
import { Card, CardBody, CardHeader, Image } from '@/components/nextui'
import { useToast } from '@/context/ToastContext'
import { useAuthHook } from '@/hooks/useAuthHook'

export const Login = (): JSX.Element => {
    const { t } = useTranslation()
    const { login: loginContext, isAuthenticated } = useAuthHook()
    const navigate = useNavigate()
    const { navigateWithUTM } = useUTMParams()
    const { showToast } = useToast()

    useEffect(() => {
        if (isAuthenticated) {
            navigateWithUTM('/initial-inscription')
        }
    }, [isAuthenticated, navigateWithUTM])

    // Helper to extract error message from response
    const extractErrorMessage = (response: any): string => {
        if (Array.isArray(response.message)) {
            return response.message[0]?.messageDesc || 'Error al iniciar sesión'
        }

        return response.message || 'Error al iniciar sesión'
    }

    // Helper to extract error message from error object
    const extractErrorFromException = (error: any): string => {
        if (error?.response?.data?.message) {
            if (Array.isArray(error.response.data.message)) {
                return (
                    error.response.data.message[0]?.messageDesc ||
                    'Error al iniciar sesión'
                )
            }

            return error.response.data.message
        }

        return error?.message || 'Error al iniciar sesión'
    }

    // Helper to handle login response
    const handleLoginResponse = (response: any): void => {
        if (response.status !== 200 && response.message) {
            const errorMessage = extractErrorMessage(response)

            showToast({
                type: 'error',
                message: errorMessage
            })
        }
    }

    // Helper to handle login error
    const handleLoginError = (error: any): void => {
        console.error('Login error:', error)
        const errorMessage = extractErrorFromException(error)

        showToast({
            type: 'error',
            message: errorMessage
        })
    }

    const onSubmit = async (data: any): Promise<void> => {
        try {
            const response = await loginContext({
                email: data.email,
                password: data.password,
                rememberMe: data.rememberMe || true
            })

            handleLoginResponse(response)
        } catch (error: any) {
            handleLoginError(error)
        }
    }

    return (
        <ContainerPage>
            <article className="p-4 bg-gradient-ean-2 dark:bg-gradient-ean">
                <motion.div
                    initial={{ y: -50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 2, ease: 'easeOut' }}
                    className="flex flex-col justify-center items-center"
                >
                    <Card className="w-full backdrop-blur-md lg:w-4/5 lg:mx-auto lg:my-0 bg-white/90 dark:bg-gray-800/90">
                        <CardHeader className="flex flex-col gap-4 justify-center items-center">
                            <Image
                                src="/LogoEAN.jpg"
                                alt="logo"
                                className="w-[150px]"
                            />
                            <SubTitle
                                className="font-normal text-center"
                                subTitle={t('login.title')}
                            />
                        </CardHeader>
                        <CardBody className="flex flex-col gap-4">
                            <LoginForm onSubmit={onSubmit} />
                        </CardBody>
                    </Card>
                </motion.div>
            </article>
        </ContainerPage>
    )
}
